import type { AxiosRequestConfig } from "axios";
import { AxiosError } from "axios";

import { isBrowser, isIntegrationTest } from "@citrine/configuration";

import { clientInstance } from "./client-instance";
import { refreshDelayMs } from "./refresh-token-expiry";
import { RequestClient, request } from "./request-client";
import { UnauthenticatedError, UnauthorizedError } from "./utils";

export type AuthData =
  { id_token: string } | { email: string; password: string };

interface RefreshData {
  refreshToken?: string;
}

export interface ITokenResponse {
  access_token: string;
}

async function makeTokenRequest(
  url: string,
  data?: RefreshData | (AuthData & { lifetime: number }),
): Promise<ITokenResponse> {
  try {
    return await request<ITokenResponse>({
      method: "POST",
      url,
      data,
    });
  } catch (error) {
    if (error instanceof AxiosError && error.response) {
      if (error.response.status >= 400 && error.response.status < 500) {
        // couldn't refresh
        throw new UnauthenticatedError(error.response);
      }
    }
    throw error;
  }
}

// ReturnType<typeof setTimeout> stays correct in both runtimes (number in the browser, Timeout in node)
let invalidationHandle: ReturnType<typeof setTimeout> | null = null;
let tokenIsInvalid: boolean = false;
function scheduleTokenInvalidation(ms: number) {
  if (invalidationHandle) {
    clearTimeout(invalidationHandle);
  }
  invalidationHandle = setTimeout(() => {
    tokenIsInvalid = true;
  }, ms);
  // let a node process exit while a refresh is pending
  if (typeof invalidationHandle === "object") {
    invalidationHandle.unref();
  }
}

let refreshResponse: Promise<ITokenResponse> | null = null;
export function refreshAccessToken(
  force?: boolean,
): Promise<ITokenResponse | void> {
  // if running integration tests, don't override authentication
  if (isIntegrationTest) {
    return Promise.resolve();
  }

  // if a current refresh request is in-flight, don't make another
  if (force || !refreshResponse) {
    refreshResponse = makeTokenRequest("/api/v1/tokens/refresh")
      .then((data) => {
        if (!isBrowser) {
          clientInstance.defaults.headers.common.Cookie = `access_token=${data.access_token}`;
        }
        tokenIsInvalid = false;
        scheduleTokenInvalidation(refreshDelayMs(data.access_token));
        return data;
      })
      .finally(() => {
        // clear the cached response, pass or fail, so the next request can refresh again
        refreshResponse = null;
      });
  }

  return refreshResponse;
}

function toClientError(error: unknown): unknown {
  if (error instanceof AxiosError) {
    if (error.response?.status === 401) {
      return new UnauthenticatedError(error.response);
    }
    if (error.response?.status === 403) {
      return new UnauthorizedError(error.response);
    }
  }
  return error;
}

/**
 * Any 401 starts re-authentication: refresh once and retry once. The retry's
 * outcome is the answer, so a second 401 surfaces as UnauthenticatedError.
 */
export async function requestWithRefresh<T>(
  config: AxiosRequestConfig,
): Promise<T> {
  if (tokenIsInvalid) {
    refreshAccessToken();
  }

  // if a refresh request is already in-flight, wait for it
  // the if-check isn't technically necessary, but we need it to avoid sync errors in jest tests
  if (refreshResponse) {
    await refreshResponse;
  }

  try {
    return await request<T>(config);
  } catch (error) {
    if (!(error instanceof AxiosError) || error.response?.status !== 401) {
      throw toClientError(error);
    }
  }

  await refreshAccessToken();
  try {
    return await request<T>(config);
  } catch (error) {
    throw toClientError(error);
  }
}

/**
 * Extend the RequestClient with automatic auth refresh
 */
export abstract class RefreshClient<
  Payload = unknown,
> extends RequestClient<Payload> {
  request<P = Payload>(config: AxiosRequestConfig): Promise<P> {
    return requestWithRefresh(config);
  }
}

export default RefreshClient;
